export type ProductVideoTypeId =
  | "hyper-motion"
  | "ugc"
  | "unboxing"
  | "tutorial"
  | "product-review"
  | "tv-spot"
  | "wild-card"
  | "ugc-virtual-try-on"
  | "pro-virtual-try-on";

// Public preset label that Higgsfield's `marketing_studio_video` accepts as `mode`.
// The server normalizes this to an internal engine id (e.g. "UGC" → "ugc",
// "Unboxing" → "ugc_unboxing", "Hyper Motion" → "product_showcase").
export type HiggsfieldMarketingStudioMode =
  | "Hyper Motion"
  | "UGC"
  | "Unboxing"
  | "Tutorial"
  | "Product Review"
  | "TV Spot"
  | "Wild Card"
  | "UGC Virtual Try On"
  | "Pro Virtual Try On";

export interface ProductVideoTypeOption {
  id: ProductVideoTypeId;
  higgsfieldMode: HiggsfieldMarketingStudioMode;
  title: string;
  shortTitle: string;
  description: string;
  preview: string;
  previewLabel: string;
  bestFor: string;
  klingFormat: string;
  promptTemplate: string;
  variantAngles: readonly string[];
  accent: string;
  selectedBorder: string;
  selectedBg: string;
}

export const DEFAULT_PRODUCT_VIDEO_TYPE_ID: ProductVideoTypeId = "ugc";

export const PRODUCT_VIDEO_TYPES = [
  {
    id: "ugc",
    higgsfieldMode: "UGC",
    title: "UGC",
    shortTitle: "UGC",
    description: "Creator-led, talk-to-camera ad with the product in hand and a real, conversational delivery.",
    preview: "https://cdn.wiserstudio.ai/static/ad-video-types/ugc.mp4",
    previewLabel: "Creator-led ad",
    bestFor: "Cold traffic on TikTok and Reels, trust-driven offers, social proof.",
    klingFormat: "Kling v3: character/reference-driven multishot with creator and product anchors.",
    promptTemplate:
      "Create a vertical UGC ad for {{productName}}. Open with a relatable creator speaking directly to camera with the product visible in hand. Use a casual, conversational hook in the first two seconds, then show one specific reason the product is worth buying with the product clearly in frame. Keep the footage handheld and authentic, with natural lighting and realistic gestures. The product must remain consistent in shape, color, label placement, and packaging across shots — treat the supplied product images as exact visual truth. End on a low-pressure recommendation. No fake UI, no invented logos, no exaggerated claims.",
    variantAngles: [
      "honest hook",
      "skeptical-to-convinced hook",
      "specific benefit hook",
    ],
    accent: "from-emerald-400 to-cyan-500",
    selectedBorder: "border-emerald-400",
    selectedBg: "bg-emerald-500/10",
  },
  {
    id: "unboxing",
    higgsfieldMode: "Unboxing",
    title: "Unboxing",
    shortTitle: "Unboxing",
    description: "First-impression package reveal with hand-held pacing, real reactions, and clean product hand-off.",
    preview: "https://cdn.wiserstudio.ai/static/ad-video-types/ugc_unboxing.mp4",
    previewLabel: "Package reveal",
    bestFor: "New launches, gifting, premium packaging, trust-building offers.",
    klingFormat: "Kling v3: image-to-video or custom multishot with creator, product, and package references.",
    promptTemplate:
      "Create a vertical UGC unboxing ad for {{productName}}. Open with a creator holding the package in a casual room, then show the product reveal, the first reaction, a quick use moment, and one clear benefit. Keep the footage handheld and authentic, with natural lighting, realistic gestures, and the supplied product images treated as exact visual truth. The product must remain consistent in shape, color, label placement, and packaging across shots. Use a conversational spoken hook if audio is available. End with a clean CTA moment. No fake UI, no invented logos.",
    variantAngles: [
      "package reveal hook",
      "first reaction hook",
      "gift-style unbox hook",
    ],
    accent: "from-cyan-400 to-blue-500",
    selectedBorder: "border-cyan-400",
    selectedBg: "bg-cyan-500/10",
  },
  {
    id: "hyper-motion",
    higgsfieldMode: "Hyper Motion",
    title: "Hyper Motion",
    shortTitle: "Hyper Motion",
    description: "Fast, cinematic product showcase with bold camera moves, kinetic transitions, and brand-grade polish.",
    preview: "https://cdn.wiserstudio.ai/static/ad-video-types/hyper-motion-mini.mp4",
    previewLabel: "Cinematic showcase",
    bestFor: "Hero launches, premium SKUs, beauty, fragrance, electronics, fashion.",
    klingFormat: "Kling v3: high-quality image-to-video with controlled camera moves and detail preservation.",
    promptTemplate:
      "Create a hyper-motion product showcase for {{productName}}. Open with a punchy reveal — push-in, orbit, or pedestal rise — then cut between macro detail beats and full-product hero frames. Lighting should feel like a premium commercial studio: rim light, controlled reflections, shallow depth of field, and confident camera motion. Treat the supplied references as exact truth for logo placement, proportions, and colors. Pace transitions to a kinetic rhythm; keep copy minimal or absent unless provided. No fake brand marks, no warped product geometry.",
    variantAngles: [
      "push-in reveal",
      "orbit hero shot",
      "macro detail intro",
    ],
    accent: "from-amber-300 to-rose-500",
    selectedBorder: "border-amber-300",
    selectedBg: "bg-amber-500/10",
  },
  {
    id: "tutorial",
    higgsfieldMode: "Tutorial",
    title: "Tutorial",
    shortTitle: "Tutorial",
    description: "Step-by-step usage sequence that makes setup, application, or assembly instantly understandable.",
    preview: "https://cdn.wiserstudio.ai/static/ad-video-types/ugc_how_to.mp4",
    previewLabel: "Step sequence",
    bestFor: "Beauty tools, kits, devices, supplements, setup-heavy products.",
    klingFormat: "Kling v3: custom multishot or image-to-video with structured step beats and stable hand interactions.",
    promptTemplate:
      "Create a step-by-step tutorial for {{productName}}. Break the action into three simple beats: prepare, use, and result. Use close-up hands and clean framing so each step is understandable without reading captions. Treat the product references as exact truth for shape, color, and packaging. Motion should be slow enough to follow but edited tightly for ads. If voiceover is available, use short instructional lines that match the visible action. Avoid clutter, unnecessary props, unsupported claims, and text overlays unless the brief provides exact copy.",
    variantAngles: [
      "three-step tutorial",
      "close-up application",
      "setup-to-result",
    ],
    accent: "from-fuchsia-400 to-pink-500",
    selectedBorder: "border-fuchsia-400",
    selectedBg: "bg-fuchsia-500/10",
  },
  {
    id: "product-review",
    higgsfieldMode: "Product Review",
    title: "Product Review",
    shortTitle: "Review",
    description: "Creator review with structured pros, hands-on demo, and a believable buy-or-skip verdict.",
    preview: "https://cdn.wiserstudio.ai/static/ad-video-types/product_review.mp4",
    previewLabel: "Creator review",
    bestFor: "Mid-funnel offers, comparison-heavy categories, considered purchases.",
    klingFormat: "Kling v3: character/reference-driven talking-head with product hand-off and macro inserts.",
    promptTemplate:
      "Create a creator review ad for {{productName}}. Show a relatable creator speaking directly to camera with the product visible in hand or on the table. Structure the video as: honest hook, why they tried it, one specific benefit with a hands-on demo, product close-up, and a low-pressure recommendation. Keep the performance natural and conversational, not scripted or overacted. Preserve the product references exactly and keep the product in-frame during the proof moment. If audio is available, use a short believable voiceover or direct-to-camera line. Avoid medical, financial, or exaggerated claims unless supplied.",
    variantAngles: [
      "honest verdict hook",
      "objection handling hook",
      "side-by-side comparison hook",
    ],
    accent: "from-orange-300 to-red-500",
    selectedBorder: "border-orange-300",
    selectedBg: "bg-orange-500/10",
  },
  {
    id: "tv-spot",
    higgsfieldMode: "TV Spot",
    title: "TV Spot",
    shortTitle: "TV Spot",
    description: "Broadcast-style commercial with structured narrative, brand voice, and high-production polish.",
    preview: "https://cdn.wiserstudio.ai/static/ad-video-types/tv-spot-mini.mp4",
    previewLabel: "Broadcast spot",
    bestFor: "Brand campaigns, top-of-funnel reach, hero launches, OOH-style placements.",
    klingFormat: "Kling v3: cinematic multishot with controlled camera language and brand-consistent palette.",
    promptTemplate:
      "Create a TV-spot-style ad for {{productName}}. Open with a brand-led visual hook, develop a single narrative beat (problem, aspiration, or transformation), and resolve on a clean hero frame of the product with brand mark. Use cinematic lighting, polished camera moves, and a consistent color palette. Treat the supplied references as exact truth for logo, packaging, and proportions. If audio is available, end with a short tagline or brand sting. Keep narration minimal; let visuals carry the story. No fake brand marks, no warped product geometry.",
    variantAngles: [
      "aspirational narrative",
      "problem-to-solution narrative",
      "brand-led hero spot",
    ],
    accent: "from-slate-300 to-indigo-500",
    selectedBorder: "border-indigo-300",
    selectedBg: "bg-indigo-500/10",
  },
  {
    id: "wild-card",
    higgsfieldMode: "Wild Card",
    title: "Wild Card",
    shortTitle: "Wild Card",
    description: "Experimental, scroll-stopping creative — pattern interrupts, surreal angles, and unexpected hooks.",
    preview: "https://cdn.wiserstudio.ai/static/ad-video-types/wild_card.mp4",
    previewLabel: "Pattern interrupt",
    bestFor: "Creative testing, breakthrough ideas, fatigued audiences, viral attempts.",
    klingFormat: "Kling v3: experimental multishot with bold camera, surreal transitions, and reference-anchored product.",
    promptTemplate:
      "Create a wild-card scroll-stopping ad for {{productName}}. Lead with an unexpected pattern interrupt in the first second — surreal scale, unusual angle, or kinetic transition — then deliver one clear product moment so the viewer knows what's being sold. Keep the product geometry, color, and labels accurate to the references even when the surrounding scene is stylized. Use punchy editing and confident sound design. Avoid clutter and avoid effects that obscure the product identity. No fake brand marks.",
    variantAngles: [
      "surreal scale hook",
      "unexpected angle hook",
      "kinetic transition hook",
    ],
    accent: "from-violet-400 to-fuchsia-500",
    selectedBorder: "border-violet-400",
    selectedBg: "bg-violet-500/10",
  },
  {
    id: "ugc-virtual-try-on",
    higgsfieldMode: "UGC Virtual Try On",
    title: "UGC Virtual Try On",
    shortTitle: "UGC Try-On",
    description: "Creator tries on or wears the product in a real, lived-in setting with natural movement.",
    preview: "https://cdn.wiserstudio.ai/static/ad-video-types/ugc_virtual_try_on.mp4",
    previewLabel: "Creator try-on",
    bestFor: "Apparel, footwear, accessories, eyewear, jewelry.",
    klingFormat: "Kling v3: character/reference multishot with consistent body and apparel rendering.",
    promptTemplate:
      "Create a UGC virtual try-on ad for {{productName}}. Show a relatable creator wearing or using the product in a real, lived-in setting (bedroom, mirror, street, café). Open with a quick hook, then show 2–3 angles of the product on the body — front, side, and a movement beat (turn, walk, gesture) — so fit and drape are believable. Keep apparel color, fabric, and details consistent with the references. Use natural lighting and casual, handheld camera. If audio is available, use a short conversational line about how it fits or feels. No warped proportions, no inconsistent garment details, no fake logos.",
    variantAngles: [
      "mirror try-on",
      "walk-through beat",
      "fit-and-fabric close-up",
    ],
    accent: "from-lime-300 to-teal-500",
    selectedBorder: "border-lime-300",
    selectedBg: "bg-lime-500/10",
  },
  {
    id: "pro-virtual-try-on",
    higgsfieldMode: "Pro Virtual Try On",
    title: "Pro Virtual Try On",
    shortTitle: "Pro Try-On",
    description: "Studio-grade try-on with model fidelity, controlled lighting, and brand-storefront polish.",
    preview: "https://cdn.wiserstudio.ai/static/ad-video-types/virtual_try_on.mp4",
    previewLabel: "Studio try-on",
    bestFor: "Fashion brand storefronts, premium apparel, lookbooks, PDP hero loops.",
    klingFormat: "Kling v3: high-quality image-to-video with stable model identity and controlled camera.",
    promptTemplate:
      "Create a pro virtual try-on for {{productName}}. Use a studio or editorial setting with a single model wearing the product. Move from a clean hero frame to two or three controlled angles — front, three-quarter, and a movement beat — that show fit, drape, and material accurately. Lighting should feel like a premium fashion shoot: soft key, controlled shadows, shallow depth of field, brand-aligned palette. Preserve apparel color, fabric, stitching, and any visible labels exactly from the references. Camera moves should be smooth and confident. No warped body proportions, no inconsistent garment details, no fake logos.",
    variantAngles: [
      "editorial hero loop",
      "three-quarter walk",
      "fabric and detail close-up",
    ],
    accent: "from-rose-300 to-purple-500",
    selectedBorder: "border-rose-300",
    selectedBg: "bg-rose-500/10",
  },
] as const satisfies readonly ProductVideoTypeOption[];

export function getProductVideoType(
  id: ProductVideoTypeId | undefined,
): ProductVideoTypeOption {
  return (
    PRODUCT_VIDEO_TYPES.find((type) => type.id === id) ??
    PRODUCT_VIDEO_TYPES.find((type) => type.id === DEFAULT_PRODUCT_VIDEO_TYPE_ID) ??
    PRODUCT_VIDEO_TYPES[0]
  );
}

// ── Per-type field map (single source of truth) ─────────────────────────────
//
// Each video type renders a different subset of the modal — UGC needs a
// creator avatar + a script, Pro Try-On needs brand voice + a model avatar,
// Hyper Motion needs only brand voice. All section gating in
// `VideoAdSetupModal` reads from `getVideoTypeFieldConfig(id)` so component
// code never string-matches on `videoTypeId`.
export type AvatarRole = "creator" | "model";

export interface VideoTypeFieldConfig {
  needsSellingPoints: boolean;
  needsAvatar: boolean;
  /** Drives copy + which avatar library to filter. */
  avatarRole: AvatarRole | null;
  needsBrand: boolean;
  needsScript: boolean;
  /** True when the legacy Banan ref-prep step should run before video
   *  dispatch. Currently false for the template flow: the ecommerce video
   *  agent receives only the product image and prompt. */
  needsAutoRefs: boolean;
}

const DEFAULT_FIELD_CONFIG: VideoTypeFieldConfig = {
  needsSellingPoints: false,
  needsAvatar: false,
  avatarRole: null,
  needsBrand: false,
  needsScript: false,
  needsAutoRefs: false,
};

// The Banan ref-prep playbook remains available in core, but template video
// generation is currently direct-to-agent: product image + prompt only.
const FIELD_CONFIG_BY_ID: Record<ProductVideoTypeId, VideoTypeFieldConfig> = {
  ugc: {
    needsSellingPoints: true,
    needsAvatar: true,
    avatarRole: "creator",
    needsBrand: false,
    needsScript: true,
    needsAutoRefs: false,
  },
  unboxing: {
    needsSellingPoints: true,
    needsAvatar: true,
    avatarRole: "creator",
    needsBrand: false,
    needsScript: false,
    needsAutoRefs: false,
  },
  "product-review": {
    needsSellingPoints: true,
    needsAvatar: true,
    avatarRole: "creator",
    needsBrand: false,
    needsScript: false,
    needsAutoRefs: false,
  },
  tutorial: {
    needsSellingPoints: true,
    needsAvatar: false,
    avatarRole: null,
    needsBrand: false,
    needsScript: false,
    needsAutoRefs: false,
  },
  "hyper-motion": {
    needsSellingPoints: false,
    needsAvatar: false,
    avatarRole: null,
    needsBrand: true,
    needsScript: false,
    needsAutoRefs: false,
  },
  "tv-spot": {
    needsSellingPoints: false,
    needsAvatar: false,
    avatarRole: null,
    needsBrand: true,
    needsScript: false,
    needsAutoRefs: false,
  },
  "wild-card": {
    needsSellingPoints: true,
    needsAvatar: false,
    avatarRole: null,
    needsBrand: false,
    needsScript: false,
    needsAutoRefs: false,
  },
  "ugc-virtual-try-on": {
    needsSellingPoints: false,
    needsAvatar: true,
    avatarRole: "model",
    needsBrand: false,
    needsScript: false,
    needsAutoRefs: false,
  },
  "pro-virtual-try-on": {
    needsSellingPoints: false,
    needsAvatar: true,
    avatarRole: "model",
    needsBrand: true,
    needsScript: false,
    needsAutoRefs: false,
  },
};

export function getVideoTypeFieldConfig(
  id: ProductVideoTypeId | undefined,
): VideoTypeFieldConfig {
  if (!id) return DEFAULT_FIELD_CONFIG;
  return FIELD_CONFIG_BY_ID[id] ?? DEFAULT_FIELD_CONFIG;
}
